import { useSession } from '@emerald-works/react-auth'
import {
  AppBar,
  Badge,
  Box,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar
} from '@mui/material'
import { AccountCircle, ExpandMore, Notifications } from '@mui/icons-material'
import { kebabCase } from 'lodash'
import React, { useRef, useState } from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'
import useGroup from '../../hooks/useGroup'
import { routes } from '../../routes'
import {
  ALLOW_ARK_USERS_TAB,
  ALLOW_CLIENT_TOOLKITS_TAB,
  ALLOW_CONSUMER_USERS_TAB,
  ALLOW_CONTENT_TAB,
  ALLOW_DASHBOARD_TAB,
  ALLOW_TEMPLATES_TAB,
  ALLOW_SKILL_BITES_TAB,
  ALLOW_ASSESSMENTS_TAB,
  ALLOW_ACQUISITION_TAB,
  ALLOW_SKILLS_PRACTICE_TAB
} from '../../util/permissions'
import { useStyles } from './style'
import { useArkMode } from '../../hooks/useArkMode'
import { ArkLogo } from '../'

const AppToolbar = () => {
  const classes = useStyles()
  const location = useLocation()
  const { user } = useSession()
  const { isInGroup } = useGroup()
  const { isAdminMode } = useArkMode()
  const [productsAnchorEl, setProductsAnchorEl] = useState(null)
  const closeTimerRef = useRef(null)

  const handleProductsEnter = (e) => {
    clearTimeout(closeTimerRef.current)
    setProductsAnchorEl(e.currentTarget)
  }

  const handleProductsLeave = () => {
    closeTimerRef.current = setTimeout(() => setProductsAnchorEl(null), 150)
  }

  const productItems = [
    { name: 'Skill Bites', route: routes.skillBiteList.path, visible: isInGroup(ALLOW_SKILL_BITES_TAB) },
    { name: 'AI Skills Practice', route: routes.skillsPracticeList.path, visible: isInGroup(ALLOW_SKILLS_PRACTICE_TAB) },
    { name: 'Assessments', route: routes.assessmentsList.path, visible: isInGroup(ALLOW_ASSESSMENTS_TAB) }
  ].filter(item => item.visible)

  const menu = [
    {
      name: 'Home',
      route: routes.home.path,
      visible: isInGroup(ALLOW_DASHBOARD_TAB)
    },
    {
      name: 'Content',
      route: routes.contentList.path,
      visible: isInGroup(ALLOW_CONTENT_TAB)
    },
    {
      name: 'Client Toolkits',
      route: routes.tenantList.path,
      visible: isInGroup(ALLOW_CLIENT_TOOLKITS_TAB)
    },
    {
      name: 'Templates',
      route: routes.templateList.path,
      visible: isInGroup(ALLOW_TEMPLATES_TAB)
    },
    {
      name: 'Acquisition',
      route: routes.acquisition.path,
      visible: isInGroup(ALLOW_ACQUISITION_TAB)
    },
    {
      name: 'Ark Users',
      route: routes.arkUsers.path,
      visible: isInGroup(ALLOW_ARK_USERS_TAB)
    },
    {
      name: 'Consumers',
      route: routes.consumerList.path,
      visible: isInGroup(ALLOW_CONSUMER_USERS_TAB)
    }
  ]

  const avatarTitle = React.useMemo(() => {
    return user?.attributes
      ? `${user.attributes.family_name}, ${user.attributes.given_name}`
      : 'User profile'
  }, [user])

  return (
    <AppBar id='app-bar' classes={{ root: classes.appBar }} color='primary'>
      <Toolbar
        id='app-toolbar'
        classes={{ root: classes.toolbar, gutters: classes.toolbarGutters }}
      >
        <Grid container alignItems='stretch'>
          <Grid
            item
            tabIndex={0}
            className={classes.toolbarItem}
            classes={{ root: classes.toolbarTitle }}
          >
            <ArkLogo fallbackText='The Ark' />
          </Grid>
          <Grid
            id='app-toolbar-menu'
            item
            className={classes.toolbarItem}
            style={{ flexGrow: 1 }}
          >
            {isAdminMode &&
              menu.map(
                (link, key) =>
                  link?.visible && (
                    <Link
                      data-test={`app-toolbar-menu-item-${kebabCase(
                        link.name
                      )}`}
                      key={key}
                      tabIndex={0}
                      underline='none'
                      label={link.name}
                      component={RouterLink}
                      to={link.route}
                      variant='subtitle2'
                      classes={{ root: classes.link }}
                      className={`${
                        location.pathname === link.route
                          ? classes.activeLink
                          : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
              )}
            {isAdminMode && productItems.length > 0 && (
              <Box
                data-test='app-toolbar-products-trigger'
                onMouseEnter={handleProductsEnter}
                onMouseLeave={handleProductsLeave}
                style={{ display: 'inline-flex', height: '100%' }}
              >
                <Link
                  data-test='app-toolbar-menu-item-products'
                  tabIndex={0}
                  underline='none'
                  variant='subtitle2'
                  classes={{ root: classes.link }}
                  className={
                    productItems.some(item => location.pathname === item.route)
                      ? classes.activeLink
                      : ''
                  }
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}
                >
                  Products
                  <ExpandMore
                    fontSize='small'
                    style={{
                      transition: 'transform 200ms',
                      transform: productsAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </Link>
                <Menu
                  anchorEl={productsAnchorEl}
                  open={Boolean(productsAnchorEl)}
                  onClose={() => setProductsAnchorEl(null)}
                  disableAutoFocusItem
                  MenuListProps={{
                    onMouseEnter: () => clearTimeout(closeTimerRef.current),
                    onMouseLeave: handleProductsLeave
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {productItems.map((item) => (
                    <MenuItem
                      key={item.name}
                      component={RouterLink}
                      to={item.route}
                      onClick={() => setProductsAnchorEl(null)}
                      selected={location.pathname === item.route}
                      data-test={`app-toolbar-products-item-${kebabCase(item.name)}`}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Grid>
          <Grid item className={classes.toolbarItem}>
            <IconButton
              id='app-toolbar-notification'
              tabIndex={0}
              aria-label='notifications'
              classes={{ label: classes.toolbarIcons }}
              size='large'
            >
              <Badge
                id='app-toolbar-notification-badge'
                badgeContent={0}
                color='secondary'
              >
                <Notifications />
              </Badge>
            </IconButton>
            <IconButton
              id='app-toolbar-profile'
              edge='end'
              tabIndex={0}
              title={avatarTitle}
              aria-label={avatarTitle}
              // aria-controls={menuId}
              aria-haspopup='true'
              // onClick={handleProfileMenuOpen}
              classes={{ label: classes.toolbarIcons }}
              size='large'
            >
              <AccountCircle fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  )
}

export default AppToolbar
